import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { etDateTimeLocalToIso, formatEtDateTime } from "@/lib/et-datetime";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  socialBulkAction,
  socialBulkCommit,
  socialBulkExport,
  socialBulkPreview,
} from "@/lib/social-bulk.functions";
import { adminListSocialPosts } from "@/lib/social-admin.functions";

export const Route = createFileRoute("/admin/social/bulk")({
  component: BulkPage,
  head: () => ({
    meta: [
      { title: "Bulk & CSV · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const TEMPLATE_CSV =
  "body,platforms,link_url,media_urls,scheduled_at,tags,is_evergreen\n" +
  '"Learn how a DSCR loan works. See the plain-English guide.",facebook|linkedin,https://dscrloanmarket.com/dscr-loans,,,"dscr|educational",false\n';

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    clean: "bg-emerald-100 text-emerald-800",
    warn: "bg-amber-100 text-amber-800",
    blocked: "bg-rose-100 text-rose-800",
  };
  return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${map[status] ?? "bg-slate-100 text-slate-700"}`}>{status}</span>;
}

function BulkPage() {
  const qc = useQueryClient();
  const preview = useServerFn(socialBulkPreview);
  const commit = useServerFn(socialBulkCommit);
  const action = useServerFn(socialBulkAction);
  const exportFn = useServerFn(socialBulkExport);
  const listPosts = useServerFn(adminListSocialPosts);

  const [csv, setCsv] = useState("");
  const [filename, setFilename] = useState("");
  const [ack, setAck] = useState(false);
  const [bypass, setBypass] = useState(false);
  const [previewData, setPreviewData] = useState<any | null>(null);
  const [committing, setCommitting] = useState(false);
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [rescheduleAt, setRescheduleAt] = useState("");

  const postsQuery = useQuery({ queryKey: ["social-posts-bulk"], queryFn: () => listPosts() });
  const posts = (postsQuery.data ?? []) as any[];
  const selectedIds = Object.keys(selection).filter((k) => selection[k]);

  async function onFile(f: File | null) {
    if (!f) return;
    setFilename(f.name);
    setCsv(await f.text());
  }

  async function runPreview() {
    if (!csv.trim()) { toast.error("Paste or upload CSV first"); return; }
    try {
      const res: any = await preview({ data: { csv, filename } });
      setPreviewData(res);
    } catch (err: any) {
      toast.error(err?.message ?? "Preview failed");
    }
  }

  async function runCommit() {
    if (!previewData) { await runPreview(); return; }
    setCommitting(true);
    try {
      const res: any = await commit({ data: { csv, filename, acknowledge_warnings: ack, bypass_dedupe: bypass } });
      toast.success(`Imported ${res.inserted} of ${res.preview.summary.total} rows`);
      setPreviewData(null);
      setCsv("");
      await qc.invalidateQueries({ queryKey: ["social-posts-bulk"] });
    } catch (err: any) {
      toast.error(err?.message ?? "Import failed");
    } finally {
      setCommitting(false);
    }
  }

  async function runExport() {
    try {
      const res: any = await exportFn({ data: {} });
      const blob = new Blob([res.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `social-posts-${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.count} rows`);
    } catch (err: any) {
      toast.error(err?.message ?? "Export failed");
    }
  }

  async function bulk(actionType: "delete" | "status" | "reschedule", extra: any = {}) {
    if (!selectedIds.length) { toast.error("Select posts first"); return; }
    if (actionType === "delete" && !confirm(`Delete ${selectedIds.length} posts?`)) return;
    try {
      const res: any = await action({ data: { ids: selectedIds, action: actionType, ...extra } });
      toast.success(`${actionType}: ${res.affected} affected`);
      setSelection({});
      await qc.invalidateQueries({ queryKey: ["social-posts-bulk"] });
    } catch (err: any) {
      toast.error(err?.message ?? "Bulk action failed");
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <h1 className="mb-2 text-2xl font-bold">Bulk & CSV</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Import posts from CSV with a dry-run compliance + duplicate preview. Export filtered posts. Apply
        bulk actions to selected rows. Every generated row runs the reverse-mortgage compliance scan.
      </p>
      <SocialSubNav />

      <section className="mb-8 rounded-lg border border-border bg-card p-4">
        <h2 className="mb-3 text-lg font-semibold">Import CSV</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Upload file</Label>
            <Input type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
            <p className="mt-1 text-xs text-muted-foreground">Columns: body, platforms (pipe-separated), link_url, media_urls, scheduled_at, tags, is_evergreen</p>
            <Button
              variant="link"
              className="h-auto p-0 text-xs"
              onClick={() => {
                const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "social-import-template.csv"; a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download template
            </Button>
          </div>
          <div>
            <Label>Or paste CSV</Label>
            <Textarea rows={6} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={TEMPLATE_CSV} />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" onClick={runPreview}>Preview</Button>
          <Button onClick={runCommit} disabled={committing || !csv.trim()}>{committing ? "Importing…" : "Import"}</Button>
          <label className="ml-4 flex items-center gap-1 text-xs">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
            Acknowledge compliance warnings
          </label>
          <label className="flex items-center gap-1 text-xs">
            <input type="checkbox" checked={bypass} onChange={(e) => setBypass(e.target.checked)} />
            Bypass 90-day duplicate guard
          </label>
        </div>

        {previewData && (
          <div className="mt-4">
            <div className="mb-2 text-sm">
              Total {previewData.summary.total} · Clean {previewData.summary.clean} · Warn {previewData.summary.warn} ·
              Blocked {previewData.summary.blocked} · Duplicates {previewData.summary.duplicates}
            </div>
            <div className="max-h-96 overflow-auto rounded border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted text-left">
                  <tr>
                    <th className="p-2">#</th>
                    <th className="p-2">Body</th>
                    <th className="p-2">Platforms</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Duplicate</th>
                    <th className="p-2">Errors / Findings</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.map((r: any) => (
                    <tr key={r.rowIndex} className="border-t border-border align-top">
                      <td className="p-2">{r.rowIndex}</td>
                      <td className="p-2 max-w-[280px] truncate" title={r.body}>{r.body}</td>
                      <td className="p-2">{(r.platforms ?? []).join(", ")}</td>
                      <td className="p-2"><StatusPill status={r.compliance_status} /></td>
                      <td className="p-2">{r.duplicate_of ? "yes" : "—"}</td>
                      <td className="p-2 max-w-[220px]">
                        {r.errors?.join("; ")}
                        {r.compliance_findings.slice(0, 3).map((f: any, i: number) => (
                          <div key={i}>[{f.severity}] {f.label}</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <section className="mb-8 rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Bulk actions on existing posts</h2>
          <Button variant="outline" size="sm" onClick={runExport}>Export CSV</Button>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="destructive" onClick={() => bulk("delete")}>Delete selected</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("status", { status: "draft" })}>Set draft</Button>
          <Input
            type="datetime-local"
            aria-label="New scheduled time, Eastern Time"
            className="w-56"
            value={rescheduleAt}
            onChange={(e) => setRescheduleAt(e.target.value)}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => bulk("reschedule", { scheduled_at: etDateTimeLocalToIso(rescheduleAt) })}
          >
            Reschedule
          </Button>
          <span className="text-xs text-muted-foreground">{selectedIds.length} selected</span>
        </div>
        <div className="max-h-96 overflow-auto rounded border border-border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted text-left">
              <tr>
                <th className="p-2 w-8">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      const next: Record<string, boolean> = {};
                      if (e.target.checked) posts.forEach((p) => (next[p.id] = true));
                      setSelection(next);
                    }}
                  />
                </th>
                <th className="p-2">Body</th>
                <th className="p-2">Status</th>
                <th className="p-2">Platforms</th>
                <th className="p-2">Scheduled</th>
              </tr>
            </thead>
            <tbody>
              {posts.slice(0, 200).map((p) => (
                <tr key={p.id} className="border-t border-border align-top">
                  <td className="p-2"><input type="checkbox" checked={!!selection[p.id]} onChange={(e) => setSelection((s) => ({ ...s, [p.id]: e.target.checked }))} /></td>
                  <td className="p-2 max-w-[320px] truncate" title={p.body}>{p.body}</td>
                  <td className="p-2">{p.status}</td>
                  <td className="p-2">{(p.platforms ?? []).join(", ")}</td>
                  <td className="p-2">{p.scheduled_at ? formatEtDateTime(p.scheduled_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
